import React, { useState, type ReactNode } from "react";
import Model from "@tripian/model";
import { useIsMobile } from "../../hooks/useIsMobile";
// Provider logos
import gygLogo from "./img/gyg.png";
import viatorLogo from "./img/viator.png";
import victoryLogo from "./img/victory.png";
import toristyLogo from "./img/toristy.png";
import bbLogo from "./img/bb.png";
import rezdyLogo from "./img/rezdy.png";
import occasionGeniusLogo from "./img/occasiongenius.png";
import { SvgIcons } from "@tripian/react";

type BaseCardProps = {
  title: string;
  price?: number;
  currency?: string;
  rating?: number;
  reviewCount?: number;
  images: string[];
  tags: string[];
  providerId: Model.PROVIDER_ID;
  onBook?: () => void;
};

type ProviderProps<T> = {
  data: T;
} & BaseCardProps;

export type TourCardProps<T> = ProviderProps<T>;

const getProviderLogo = (providerId: Model.PROVIDER_ID): ReactNode => {
  const logoClasses = "h-6 w-auto object-contain";
  const providerNameClasses = "hidden md:block text-sm text-[#696969]";
  const fallbackNameClasses = "text-sm text-[#696969]";

  switch (providerId) {
    case Model.PROVIDER_ID.GYG:
      return (
        <div className="flex items-center gap-2">
          <img src={gygLogo} alt="GetYourGuide" className={logoClasses} />
          <span className={providerNameClasses}>GetYourGuide</span>
        </div>
      );
    case Model.PROVIDER_ID.VIATOR:
      return (
        <div className="flex items-center gap-2">
          <img src={viatorLogo} alt="Viator" className={logoClasses} />
          <span className={providerNameClasses}>Viator</span>
        </div>
      );
    case Model.PROVIDER_ID.VICTORY:
      return (
        <div className="flex items-center gap-2">
          <img src={victoryLogo} alt="Victory Live" className={logoClasses} />
          <span className={providerNameClasses}>Victory Live</span>
        </div>
      );
    case Model.PROVIDER_ID.TORISTY:
      return (
        <div className="flex items-center gap-2">
          <img src={toristyLogo} alt="Toristy" className={logoClasses} />
          <span className={providerNameClasses}>Toristy</span>
        </div>
      );
    case Model.PROVIDER_ID.BOOK_BARBADOS:
      return (
        <div className="flex items-center gap-2">
          <img src={bbLogo} alt="BookBarbados" className={logoClasses} />
          <span className={providerNameClasses}>BookBarbados</span>
        </div>
      );
    case Model.PROVIDER_ID.REZDY:
      return <img src={rezdyLogo} alt="Rezdy" className={logoClasses} />;
    case Model.PROVIDER_ID.OCCASION_GENIUS:
      return (
        <div className="flex items-center gap-2">
          <img src={occasionGeniusLogo} alt="OccasionGenius" className={logoClasses} />
        </div>
      );
    // Logosu olmayan provider'lar en azından adıyla görünsün; hiçbir şey basmamak
    // kartı kimliksiz bırakıyordu. providerNameClasses mobilde gizli olduğu için
    // (logo görünürken adı saklamak içindi) burada ayrı bir sınıf kullanılıyor.
    case Model.PROVIDER_ID.LIVN:
      return <span className={fallbackNameClasses}>Livn</span>;
    case Model.PROVIDER_ID.GOCITY:
      return <span className={fallbackNameClasses}>GoCity</span>;
    case Model.PROVIDER_ID.CIVITATIS:
      return <span className={fallbackNameClasses}>Civitatis</span>;
    default:
      return null;
  }
};

const TourCard = <T extends unknown>({
  title,
  price,
  currency = "$",
  rating,
  reviewCount,
  images,
  tags,
  providerId,
  onBook,
}: TourCardProps<T>) => {
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const isMobile = useIsMobile();

  const nextSlide = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsImageLoading(true);
    setActiveSlideIndex(prev => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsImageLoading(true);
    setActiveSlideIndex(prev => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const goToSlide = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    setIsImageLoading(true);
    setActiveSlideIndex(index);
  };

  const handleImageLoad = () => {
    setIsImageLoading(false);
  };

  const currentImageUrl = images.length > 0 ? images[activeSlideIndex] : "";

  return (
    <div
      className="group mb-4 flex flex-row overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:shadow-md cursor-pointer md:cursor-default"
      onClick={e => {
        e.stopPropagation();
        if (isMobile && onBook) {
          onBook();
        }
      }}
    >
      {/* Sol taraf - Resim */}
      <div className="relative h-[120px] sm:h-[130px] md:h-[140px] lg:h-[180px] w-[120px] sm:w-[140px] md:w-[180px] lg:w-[260px] flex-shrink-0 overflow-hidden">
        <div className="w-full h-full relative">
          {currentImageUrl ? (
            <div className="relative w-full h-full">
              <img
                src={currentImageUrl}
                alt={title}
                className={`h-full w-full object-cover transition-all duration-300 ease-in-out rounded-r-2xl ${isImageLoading ? "opacity-40" : "opacity-100"}`}
                loading="lazy"
                onLoad={handleImageLoad}
              />

              {/* Resim yüklenirken loader göster */}
              {isImageLoading && (
                <div className="absolute inset-0 flex items-center justify-center z-20 bg-black/10">
                  <div className="w-10 h-10 border-[3px] border-white border-t-transparent rounded-full animate-spin shadow-md"></div>
                </div>
              )}
            </div>
          ) : (
            <div className="h-full w-full bg-gray-100 flex flex-col items-center justify-center rounded-l-2xl">
              <SvgIcons.NoImage className="w-12 h-12 text-gray-400 mb-2" />
              <span className="text-xs text-gray-400">No Image</span>
            </div>
          )}

          {/* Slider navigasyon okları - birden fazla resim varsa göster */}
          {images.length > 1 && (
            <>
              {/* Sol ok */}
              <button
                onClick={prevSlide}
                className="absolute left-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white bg-opacity-70 shadow-sm flex items-center justify-center z-10 hover:bg-opacity-90 transition-all"
              >
                <SvgIcons.ChevronLeft className="w-4 h-4 text-gray-700" />
              </button>

              {/* Sağ ok */}
              <button
                onClick={nextSlide}
                className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white bg-opacity-70 shadow-sm flex items-center justify-center z-10 hover:bg-opacity-90 transition-all"
              >
                <SvgIcons.ChevronRight className="w-4 h-4 text-gray-700" />
              </button>
            </>
          )}

          {/* Slider noktaları göstergesi */}
          {images.length > 1 && (
            <div className="absolute bottom-1 left-0 right-0 flex justify-center gap-1 z-10">
              {images.map((_, index) => (
                <button
                  key={`slider-dot-${index}`}
                  onClick={e => goToSlide(e, index)}
                  className={`w-1.5 h-1.5 rounded-full ${index === activeSlideIndex ? "bg-white" : "bg-white bg-opacity-50"}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Sağ taraf - İçerik */}
      <div className="flex flex-1 flex-col p-2 sm:p-3 lg:p-4 justify-between overflow-hidden">
        <div>
          <div className="flex items-center justify-between mb-1">
            {/* Provider Logo ve İsim */}
            <div className="flex items-center justify-center overflow-hidden">
              {getProviderLogo(providerId)}
            </div>

            {/* Book butonu ve Fiyat */}
            <div className="flex items-center gap-2">
              {price && (
                <span className="text-sm font-semibold text-gray-900">
                  {currency === "USD" ? "$" : currency} {price.toLocaleString()}
                </span>
              )}
              <button
                className="hidden md:inline-flex items-center justify-center h-6 sm:h-8 px-2 sm:px-4 rounded-full border border-gray-300 bg-primary-color text-white transition-colors min-w-[60px] sm:min-w-[80px]"
                onClick={e => {
                  e.stopPropagation();
                  if (onBook) onBook();
                }}
              >
                <span className="text-xs sm:text-sm font-medium">Book</span>
              </button>
            </div>
          </div>

          {/* İsim */}
          <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 mb-1 lg:mb-2 line-clamp-2">
            {title}
          </h3>
        </div>

        {/* Alt kısım */}
        <div className="flex flex-col gap-2">
          {/* Rating ve Tags aynı satırda */}
          <div className="flex items-center gap-2 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            {/* Rating */}
            {typeof rating === "number" && rating > 0 && (
              <div className="flex items-center">
                <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-gray-100">
                  <span className="text-xs sm:text-sm font-medium whitespace-nowrap">
                    {rating ? rating.toFixed(1) : ""}
                  </span>
                  <SvgIcons.Star className="w-4 h-4 sm:w-4 pb-[0.125rem] text-yellow-400" />
                  {reviewCount && reviewCount > 0 && (
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      ({reviewCount})
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Tags */}
            {tags.length > 0 && (
              <div className="flex gap-1 whitespace-nowrap">
                {tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex flex-shrink-0 items-center px-3 py-1 rounded-full bg-gray-100 text-xs sm:text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TourCard;
